import React from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
} from "@mui/material";
import getAvatarInitials from "utils/getAvatarInitials";
import { CustomerLabel } from "../Common/CustomerLabel";
import { ANTooltip, CroppedAvatar } from "@answersnow/pkg-theme";
import { translateRelationship } from "../utils";


interface CareTeamMemberProps {
  member: {
    id: number;
    userId: string | null;
    teamId: number;
    teamName: string;
    roleId: number;
    roleName: string;
    email: string;
    createdAt: string;
    updatedAt: string | null;
    firstName: string | null;
    lastName: string | null;
    name: string | null;
    documentSigner: boolean | null;
    legalGuardian: boolean | null;
    hasAcceptedInvite: boolean;
    timezone?: string;
    familyId?: string;
    phone?: string;
    city?: string;
    state?: string;
    country?: string;
    zip?: string;
    address?: string;
    address2?: string;
    dateOfBirth?: string | null;
    gender?: string;
    relationship?: string | null;
    invitationGuid?: string;
    expirationDate?: string;
    imageUrl?: string;
    image_url?: string;
  };
}

export const CareTeamMember: React.FC<CareTeamMemberProps> = ({ member }) => {
  const { t } = useTranslation(["clientHeader"]);

  const getDisplayName = () => {
    if (member.name) return member.name;
    if (member.firstName && member.lastName) return `${member.firstName} ${member.lastName}`;
    if (member.firstName) return member.firstName;
    if (member.lastName) return member.lastName;
    return null;
  };

  const getInitials = () => {
    if (member.name) return getAvatarInitials(member.name);
    if (member.firstName && member.lastName) return getAvatarInitials(`${member.firstName} ${member.lastName}`);
    if (member.firstName) return getAvatarInitials(member.firstName);
    return member.email.charAt(0).toUpperCase();
  };

  const getStatus = () => {
    if (member.hasAcceptedInvite === false) return t("clientHeader:careTeam.expiredInvite");
    return null;
  };

  const avatarUrl = member.imageUrl || member.image_url;

  return (
    <Box sx={{ padding: "24px 0 0 0" }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <CroppedAvatar
          src={avatarUrl}
          alt={getDisplayName() || member.email}
          sx={{
            width: 40,
            height: 40,
            backgroundColor: "#8E42CA",
            color: "#ffffff",
            mr: 2,
          }}
        >
          {getInitials()}
        </CroppedAvatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <ANTooltip component={Typography} variant="body1">
            {getDisplayName()}
          </ANTooltip>
          <ANTooltip component={Typography} variant="body2" color="text.secondary">
            {member.email}
          </ANTooltip>
          {getStatus() && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontStyle: "italic" }}
            >
              {getStatus()}
            </Typography>
          )}
        </Box>
      </Box>

      <Box>
        <CustomerLabel
          label={t("clientHeader:profile.relationship")}
          value={translateRelationship(t, member.roleName)}
        />
        <CustomerLabel
          label={t("clientHeader:profile.legalGuardian")}
          value={member.legalGuardian ? t("clientHeader:profile.yes") : t("clientHeader:profile.no")}
        />
        <CustomerLabel
          label={t("clientHeader:profile.designatedCaregiver")}
          value={member.documentSigner ? t("clientHeader:profile.yes") : t("clientHeader:profile.no")}
        />
      </Box>
    </Box>
  );
};
